import { useEffect, useState } from 'react';

const searchMap = new Map<string | object, object>();
const debounceMap = new Map<any, any>();

export interface ISearch<T> {
  setSearch: (key: keyof T, value: any) => void;
  resetSearch: () => void;
  /** Debounced values — what queries and filters should read. */
  search: T;
  /**
   * Immediate values, updated on every keystroke. Bind controlled inputs to this:
   * binding them to `search` makes the field lag its own typing by the delay.
   */
  searchInput: T;
}

export interface ConfigOptionsProps {
  delay?: number;
}

interface ConfigProps<T extends object> {
  key?: string;
  options?: {
    [key in keyof T]: ConfigOptionsProps;
  };
}

export function useSearch<T extends object>(search: T, config?: ConfigProps<T>): ISearch<T> {
  const data = searchMap.get(config?.key || search) as T;

  const [searchState, setSearchState] = useState<T>(data ?? search);
  const [searchDebouncedState, setDebouncedState] = useState<T>(data ?? search);

  /**
   * This will set the search values in state
   * @param key based on key of your object
   * @param value
   */
  function setSearch(key: keyof T, value: any) {
    const hasDebounce = debounceMap.get(key);

    if (hasDebounce) {
      clearTimeout(hasDebounce);
    }

    setSearchState((current) => {
      const newValue = { ...current, [key as string]: value };

      if (config?.options?.[key as keyof T]?.delay) {
        const debounce = setTimeout(() => {
          setDebouncedState(newValue);
          clearTimeout(debounce);
          debounceMap.delete(key);
        }, config?.options?.[key as keyof T]?.delay);

        debounceMap.set(key, debounce);
        return newValue;
      }

      setDebouncedState(newValue);
      return newValue;
    });
  }

  /**
   * This will reset the state values to default.
   * Cancels any in-flight debounce, so a reset cannot be overwritten a moment
   * later by the keystroke that was still pending.
   */
  const resetSearch = () => {
    debounceMap.forEach((timeout) => clearTimeout(timeout));
    debounceMap.clear();

    setSearchState(search);
    setDebouncedState(search);
  };

  /**
   *  if un-mounted. the state object will save to map
   */
  useEffect(
    () => () => {
      searchMap.set(config?.key || search, searchState);
    },
    [config?.key, search, searchState],
  );

  return {
    setSearch,
    resetSearch,
    search: searchDebouncedState,
    searchInput: searchState,
  };
}
